import { useSuspenseQuery } from "@tanstack/react-query";
import { IconTasks } from "@wilco/ui";
import { useEffect, useState, type CSSProperties } from "react";
import { formatNumber, t } from "../i18n.ts";
import { TargetLink } from "../shell/targets.tsx";
import { deskQuery, type Tile } from "./desk.ts";

// Плитки поднимаются по очереди только на первом столе после загрузки
// страницы; возврат на стол не повторяет (chmk_shell, desk.js, arrived).
let arrived = false;
const MAX_STAGGER = 12;

export function DeskPage() {
  const { data: desk } = useSuspenseQuery(deskQuery);
  // В StrictMode тело компонента идёт дважды: флаг ставит эффект, а не рендер.
  const [arrive] = useState(() => !arrived);
  useEffect(() => {
    arrived = true;
  }, []);
  let order = 0;
  const next = () => Math.min(order++, MAX_STAGGER);
  const { tasks } = desk;

  return (
    <div className="page desk" data-arrive={arrive ? "" : undefined}>
      <h1 className="page-title">{t("desk.title")}</h1>

      {tasks ? (
        <button type="button" className="tile tile-tasks w-cut" data-tone={tasks.overdue ? "danger" : "primary"} style={{ "--i": next() } as CSSProperties}>
          <IconTasks className="tile-icon" />
          <span className="tile-count w-num">{formatNumber(tasks.count)}</span>
          <span className="tile-tasks-text">
            <span className="tile-title">{t("desk.tasks")}</span>
            <span className="tile-tasks-split">
              {tasks.overdue ? <span className="tile-tasks-overdue">{t("desk.tasks.overdue", { count: tasks.overdue })}</span> : null}
              {tasks.today ? <span>{t("desk.tasks.today", { count: tasks.today })}</span> : null}
              {tasks.week ? <span>{t("desk.tasks.week", { count: tasks.week })}</span> : null}
            </span>
          </span>
        </button>
      ) : null}

      {desk.groups.map((group) => (
        <section key={group.key} className="desk-group" aria-labelledby={`desk-${group.key}`}>
          <h2 id={`desk-${group.key}`} className="desk-group-title">
            {t(group.title)}
          </h2>
          <div className="tiles">
            {group.tiles.map((tile) => (
              <TileLink key={tile.key} tile={tile} index={next()} />
            ))}
          </div>
        </section>
      ))}

      <p className="desk-stub w-muted">{t("desk.stub")}</p>
    </div>
  );
}

function TileLink({ tile, index }: { tile: Tile; index: number }) {
  const Icon = tile.icon;
  // Пусто: число серым, глаз цепляется только за то, где есть работа.
  const tone = tile.count === 0 ? "muted" : tile.tone;
  return (
    <TargetLink dest={tile.target} className="tile w-cut" data-tone={tone} style={{ "--i": index } as CSSProperties}>
      {tile.count === null ? (
        // Экран-сканер без счётчика: значок крупно на месте числа.
        <Icon className="tile-glyph" />
      ) : (
        <>
          <Icon className="tile-icon" />
          <span className="tile-count w-num">{formatNumber(tile.count)}</span>
        </>
      )}
      <span className="tile-title">{t(tile.title)}</span>
    </TargetLink>
  );
}
